/**
 * @file Utility function to transform every single value of an object, leaving
 * the keys intact, and filtering out any `undefined` values.
 */

/**
 * Transforms the values of an object into a new object, filtering out any undefined values.
 * @param object The object to transform
 * @param transform The function mapping the old value to the new value, or `undefined` to filter it out
 * @returns A new object whose values are the result of the transformation of
 * the corresponding key from the original object, excluding any `undefined` values .
 */
function compactMapValues<TObject extends object, TNewValue>(
  object: TObject,
  transform: (value: TObject[keyof TObject], key: keyof TObject) => TNewValue | undefined,
): {
  [TKey in keyof TObject]: TNewValue
} {
  const mappedObject = {} as Partial<{ [TKey in keyof TObject]: TNewValue }>
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      const newValue = transform(object[key], key)
      if (newValue !== undefined) {
        mappedObject[key] = newValue
      }
    }
  }
  return mappedObject as { [TKey in keyof TObject]: TNewValue }
}

export default compactMapValues
